import {
  saveBlobWithNativeApp,
  saveUrlWithNativeApp
} from '@/app/plugin/fileSaver';
import { isNativeAppRuntime } from '@/biz/tool';

type DownloadMediaKind = 'image' | 'video' | 'file';

export type DownloadResult = {
  method: 'native' | 'browser';
  canceled?: boolean;
};

export type DownloadUrlOptions = {
  requestId?: string;
  url: string;
  fileName: string;
  mimeType?: string;
  mediaKind?: DownloadMediaKind;
  saveToPhotos?: boolean;
};

export type DownloadBlobOptions = {
  blob: Blob;
  fileName: string;
  mimeType?: string;
  mediaKind?: DownloadMediaKind;
  saveToPhotos?: boolean;
};

export type DownloadWithProgressOptions = Omit<DownloadBlobOptions, 'blob'> & {
  url: string;
  onProgress?: (progress: number) => void;
};

function downloadUrlInBrowser({
  url,
  fileName
}: Pick<DownloadUrlOptions, 'url' | 'fileName'>): DownloadResult {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return { method: 'browser' };
}

function downloadBlobInBrowser(blob: Blob, fileName: string): DownloadResult {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { method: 'browser' };
}

export async function downloadUrl(
  options: DownloadUrlOptions
): Promise<DownloadResult> {
  if (isNativeAppRuntime()) {
    const nativeResult = await saveUrlWithNativeApp(options);
    if (nativeResult) return nativeResult;
  }

  return downloadUrlInBrowser(options);
}

export async function downloadBlob(
  options: DownloadBlobOptions
): Promise<DownloadResult> {
  if (isNativeAppRuntime()) {
    const nativeResult = await saveBlobWithNativeApp(options);
    if (nativeResult) return nativeResult;
  }

  return downloadBlobInBrowser(options.blob, options.fileName);
}

export const downloadWithProgress = ({
  url,
  fileName,
  mimeType,
  mediaKind,
  saveToPhotos,
  onProgress
}: DownloadWithProgressOptions): Promise<DownloadResult> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob';

    xhr.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    };

    xhr.onerror = () => reject(new Error('Download failed.'));
    xhr.onabort = () => reject(new Error('Download canceled.'));

    xhr.onload = async () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error('Download failed.'));
        return;
      }

      try {
        const blob = xhr.response as Blob;
        const result = await downloadBlob({
          blob,
          fileName,
          mimeType: mimeType || blob.type || 'application/octet-stream',
          mediaKind,
          saveToPhotos
        });
        onProgress?.(100);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    xhr.send();
  });
